import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSidebar } from '../context/SidebarContext';
import Header from '../components/Header';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import ExamListModal from '../components/ExamResult/ExamListModal';
import ResultInputModal from '../components/ExamResult/ResultInputModal';
import { Printer, FileText, CheckCircle2, PlayCircle, FilePlus, ArrowLeft, HelpCircle, Menu, Link as LinkIcon, ChartNoAxesCombined } from 'lucide-react';
import FloatingButton from '../components/common/FloatingButton';
import CreateExamModal from '../components/ExamResult/CreateExamModal';
import { handlePDF } from '../components/ProblemPrint/handlePDFv8';
import { handleSolutionPDF } from '../components/SolutionPrint/handleSolutionPDFv2';
import WrongAnswerModal from '../components/ExamResult/WrongAnswerModal';
import ProblemPreview from '../components/ProblemPreview';
import { PrintPreviewModal } from '../components/printPreview';
import ReportModal from '../components/ReportModal';
import { Button, Modal } from '../components/common';
import { LoadingSpinner } from '../components/common/StatusComponents';

import PresetModal from '../components/PresetPage/PresetModal';

function ExamResult() {
  const { student_id, stamp_id, exam_id } = useParams();
  const { getAccessToken } = useAuth();
  const { isExpanded } = useSidebar();
  const navigate = useNavigate();
  const [examList, setExamList] = useState([]);
  const [currentExam, setCurrentExam] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExamIndex, setSelectedExamIndex] = useState(0);

  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const floatingButtonRef = useRef(null);
  const [floatingButtonRect, setFloatingButtonRect] = useState(null);
  const [treeData, setTreeData] = useState(null);
  const [isDepthDropdownOpen, setIsDepthDropdownOpen] = useState(false);
  const depthDropdownRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDepth, setSelectedDepth] = useState('d4');
  const [isCreateExamModalOpen, setIsCreateExamModalOpen] = useState(false);
  const [selectedExamId, setSelectedExamId] = useState(null);
  const [selectedAddressesForExam, setSelectedAddressesForExam] = useState([]);
  const [isHeaderFloating, setIsHeaderFloating] = useState(false);
  const examCardRef = useRef(null);
  const [isMobileView, setIsMobileView] = useState(() => window.innerWidth < 768);
  const [problemCounts, setProblemCounts] = useState({});
  const [examType, setExamType] = useState('alpha');
  const [deltaSelections, setDeltaSelections] = useState([]);
  const [hierarchicalSelections, setHierarchicalSelections] = useState({
    d1: new Set(),
    d2: new Set(),
    d3: new Set(),
    d4: new Set()
  });
  const modalRef = useRef();
  const [isWrongAnswerModalOpen, setIsWrongAnswerModalOpen] = useState(false);
  const [showOnlyProblems, setShowOnlyProblems] = useState(false);
  const [previewInfo, setPreviewInfo] = useState({
    isOpen: false,
    problemId: [],
    problemNumber: null,
    isCorrect: null
  });
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  const [analysis, setAnalysis] = useState({
    target_grade: 0,
    progress: 0.0,
    percentage: 0.04,
    remain_time: 42240
  });
  
  // 리포트 모달 관련 상태 추가
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  
  // 툴팁 관련 상태 추가
  const [showTooltip, setShowTooltip] = useState(false);

  // 프리셋 존재 여부 상태 (null=미확인, true=존재, false=삭제됨)
  const [presetExists, setPresetExists] = useState(null);

  // 프리셋 생성 모달 상태
  const [isCreatePresetModalOpen, setIsCreatePresetModalOpen] = useState(false);
  
  // 모바일 스크롤 참조를 위한 ref
  const mobileScrollRef = useRef(null);

  // report_status에 따른 아이콘 색상과 툴팁을 반환하는 함수
  const getReportStatusInfo = (status) => {
    switch (status) {
      case 0:
        return { color: '!bg-gray-400', textColor: 'text-white', tooltip: '리포트 없음' };
      case 100:
        return { color: '!bg-yellow-500', textColor: 'text-gray-900', tooltip: '생성 요청됨' };
      case 101:
        return { color: '!bg-orange-500', textColor: 'text-white', tooltip: '생성 중' };
      case 102:
        return { color: '!bg-red-500', textColor: 'text-white', tooltip: '생성 오류' };
      case 150:
        return { color: '!bg-purple-500', textColor: 'text-white', tooltip: '편집 중' };
      case 200:
      case 998:
        return { color: '!bg-blue-500', textColor: 'text-white', tooltip: '준비 완료(미확정)' };
      case 999:
        return { color: '!bg-primary', textColor: 'text-white', tooltip: '확정 완료' };
      default:
        return { color: '!bg-gray-400', textColor: 'text-white', tooltip: '상태 불명' };
    }
  };

  const colors = {
    100: { color: 'bg-danger' },
    101: { color: 'bg-danger/30' },
    200: { color: 'bg-primary' },
    201: { color: 'bg-primary/30' },
    500: { color: 'bg-warning/30' },
    0: { color: 'after:content-["×"]' }
  };

  const depthTitles = {
    'd1': '대단원',
    'd2': '소단원',
    'd3': '대유형',
    'd4': '중유형'
  };

  // 윈도우 리사이즈 이벤트 리스너 추가
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);


  // currentExam 변경 시 커스텀 프리셋 존재 여부 확인
  useEffect(() => {
    if (currentExam?.exam_info?.exam_type === 'custom' && currentExam.exam_info.preset_name) {
      if (!currentExam.exam_info.preset_id) {
        setPresetExists(false);
        return;
      }
      const checkPresetExists = async () => {
        try {
          setPresetExists(null);
          const token = await getAccessToken();
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/exams/presets/${currentExam.exam_info.preset_id}`,
            { headers: { 'Authorization': token } }
          );
          if (response.ok) {
            setPresetExists(true);
          } else {
            setPresetExists(false);
          }
        } catch {
          setPresetExists(false);
        }
      };
      checkPresetExists();
    } else {
      setPresetExists(null);
    }
  }, [currentExam, getAccessToken]);

  // 모바일에서 테이블이 렌더링될 때 선택된 depth의 컬럼이 보이도록 스크롤 조정
  useEffect(() => {
    if (mobileScrollRef.current && window.innerWidth < 768) {
      // 테이블이 완전히 렌더링된 후 스크롤 조정을 위해 setTimeout 사용
      setTimeout(() => {
        if (mobileScrollRef.current) {
          const table = mobileScrollRef.current.querySelector('table');
          if (table) {
            const headers = table.querySelectorAll('thead th');
            if (headers.length > 0) {
              // 선택된 depth에 따라 스크롤할 컬럼 결정
              let targetHeader = null;
              const visibleColumns = getVisibleColumns();
              
              // 현재 depth에 해당하는 컬럼 찾기
              if (selectedDepth === 'd4' && visibleColumns.includes('name')) {
                // 중유형인 경우 마지막 컬럼
                targetHeader = headers[headers.length - 1];
              } else if (selectedDepth === 'd3' && visibleColumns.includes('name')) {
                // 대유형인 경우 마지막 컬럼
                targetHeader = headers[headers.length - 1];
              } else if (selectedDepth === 'd2' && visibleColumns.includes('name')) {
                // 소단원인 경우 마지막 컬럼
                targetHeader = headers[headers.length - 1];
              } else if (selectedDepth === 'd1') {
                // 대단원인 경우 첫 번째 컬럼
                targetHeader = headers[0];
              }
              
              if (targetHeader) {
                // 해당 컬럼의 왼쪽 위치로 스크롤
                mobileScrollRef.current.scrollLeft = targetHeader.offsetLeft;
              }
            }
          }
        }
      }, 100);
    }
  }, [selectedDepth, treeData]);

  const fetchExamList = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp_id}`, {
        headers: { 'Authorization': token }
      });

      if (response.status === 204) {
        // console.log('No exams found (204 response)'); // 디버깅용
        setExamList([]);
        setCurrentExam(null);
        setTreeData(null);
        setSelectedExamIndex(0);
        setSelectedExamId(null);
        setProblemCounts({});
        setLoading(false); // 로딩 상태를 false로 설정
        return;
      }

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      // DEBUG: exam 객체 필드 확인 (exam_round 포함 여부)
      // if (data.data?.length > 0) console.log('[ExamResult] 첫 번째 exam 객체 키:', Object.keys(data.data[0]), data.data[0]);
      setExamList(data.data);

      // URL에 exam_id가 있는 경우
      if (exam_id) {
        const examIndex = data.data.findIndex(exam => exam.exam_id === parseInt(exam_id));
        if (examIndex !== -1) {
          setSelectedExamIndex(examIndex);
          const targetExam = data.data[examIndex];

          // 단순히 선택한 시험을 로드 (examList 전달)
          fetchExamDetail(targetExam.exam_id, data.data);
          return;
        }
      }

      // URL에 exam_id가 없거나 찾지 못한 경우 최신 시험 정보 가져오기
      if (data.data.length > 0) {
        setSelectedExamIndex(0);
        const latestExam = data.data[0];

        // 단순히 최신 시험을 로드 (examList 전달)
        fetchExamDetail(latestExam.exam_id, data.data);
      }
    } catch (error) {
      console.error('시험 목록 조회 오류:', error);
      setLoading(false); // 로딩 상태를 false로 설정
    }
  };

  const fetchExamDetail = async (examId, examListData = null) => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp_id}/${examId}`, {
        headers: { 'Authorization': token }
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      // console.log('Exam detail response:', data.data); // 디버깅용
      setCurrentExam(data.data);
      
      // 결과가 없는 시험인 경우 이전 확정 시험의 결과맵을 로드
      if (data.data.exam_info.exam_status !== 999) {
        // examListData가 전달되면 사용, 아니면 state의 examList 사용
        const listToUse = examListData || examList;
        const previousConfirmedExam = listToUse
          .filter(exam =>
            exam.exam_status === 999 &&
            new Date(exam.exam_created_at) < new Date(data.data.exam_info.exam_created_at)
          )
          .sort((a, b) => new Date(b.exam_created_at) - new Date(a.exam_created_at))[0];

        if (previousConfirmedExam) {
          // console.log('이전 확정 시험 찾음:', previousConfirmedExam.exam_id, '현재 시험:', examId);
          fetchResultMap(previousConfirmedExam.exam_id, examId, listToUse); // 이전 확정 시험의 결과맵 + 현재 시험 문제 표시
        } else {
          // console.log('이전 확정 시험 없음, 현재 시험으로 결과맵 로드');
          fetchResultMap(examId, null, listToUse); // 이전 확정 시험이 없으면 현재 시험으로
        }
      } else {
        // console.log('확정된 시험, 자체 결과맵 사용');
        fetchResultMap(examId, null, examListData || examList); // 확정된 시험은 자체 결과맵 사용
      }
    } catch (error) {
      console.error('시험 상세 정보 조회 오류:', error);
      setLoading(false); // 로딩 상태를 false로 설정
    }
  };

  const handleExamChange = (direction) => {
    const newIndex = selectedExamIndex + (direction === 'next' ? -1 : 1);
    if (newIndex >= 0 && newIndex < examList.length) {
      setSelectedExamIndex(newIndex);
      const newExam = examList[newIndex];

      // 상태가 999인지 확인하고 아니면 이전 확정 시험 찾기
      if (newExam.exam_status !== 999) {
        const previousConfirmedExam = examList
          .filter(exam =>
            exam.exam_status === 999 &&
            new Date(exam.exam_created_at) < new Date(newExam.exam_created_at)
          )
          .sort((a, b) => new Date(b.exam_created_at) - new Date(a.exam_created_at))[0];

        if (previousConfirmedExam) {
          fetchExamDetail(newExam.exam_id); // 카드는 선택한 시험 정보로
          fetchResultMap(previousConfirmedExam.exam_id, newExam.exam_id, examList); // 이전 확정 시험의 결과맵 + 현재 시험 문제 표시
          return;
        }
      }

      // 999 상태이거나 이전 확정 시험이 없는 경우
      fetchExamDetail(newExam.exam_id);
      fetchResultMap(newExam.exam_id, null, examList);
    }
  };

  const handleSelectExam = (examId, index) => {
    setSelectedExamIndex(index);
    fetchExamDetail(examId);
    setIsModalOpen(false);
  };

  useEffect(() => {
    fetchExamList();

  }, [stamp_id, exam_id]);

  // 외부 클릭 시 툴팁 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTooltip && !event.target.closest('.tooltip-container')) {
        setShowTooltip(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDepthDropdownOpen && 
          !event.target.closest('.depth-dropdown-container') &&
          !event.target.closest('.depth-dropdown-menu')) {
        setIsDepthDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDepthDropdownOpen]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsHeaderFloating(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '-100px 0px 0px 0px'
      }
    );

    if (examCardRef.current && currentExam) {
      observer.observe(examCardRef.current);
    }

    return () => {
      if (examCardRef.current) {
        observer.unobserve(examCardRef.current);
      }
    };
  }, [currentExam]);

  const getExamStatusText = (status) => {
    switch (status) {
      case 200: return "결과입력필요";
      case 400: return "결과입력완료";
      case 999: return "결과확정완료";
      default: return "상태 미정";
    }
  };

  const getExamStatusIcon = (status) => {
    switch (status) {
      case 999:
        return <CheckCircle2 className="text-green-600" size={32} />;
      case 400:
        return <PlayCircle className="text-orange-500" size={32} />;
      case 200:
      default:
        return <FilePlus className="text-blue-500" size={32} />;
    }
  };

  // 프리셋 생성을 위한 stamp 정보 추출
  const getPresetStamp = () => {
    if (!treeData?.field_info?.d1_y_axis?.[0]?.[2]) return null;
    return {
      tree_id: treeData.field_info.d1_y_axis[0][2].split('/')[0],
      tree_name: currentExam?.exam_info?.tree_name || '',
      edu_ver: currentExam?.exam_info?.edu_ver || ''
    };
  };

  // 자동 프리셋 이름 생성
  const getAutoPresetName = () => {
    const treeName = currentExam?.exam_info?.tree_name || '';
    const studentName = currentExam?.student_info?.student_name || '';
    const now = new Date();
    const date = now.toISOString().slice(2, 10).replace(/-/g, '');
    const hhmm = String(now.getHours()).padStart(2, '0') + String(now.getMinutes()).padStart(2, '0');
    return `${treeName}_${studentName}_${date}_${hhmm}`;
  };

  // 프리셋 저장 성공 → 시험지제작 페이지로 이동
  const handlePresetSaveSuccess = (data) => {
    setIsCreatePresetModalOpen(false);
    const presetId = data?.preset_id || data?.data?.preset_id;
    if (presetId) {
      navigate(`/preset?highlight=${presetId}`);
    } else {
      navigate('/preset');
    }
  };

  // 출제변경: 기존 시험 삭제 → 새 시험 생성(preset) → 목록 새로고침
  // 반환값: true=완료, false=취소/실패 (PresetModal에서 모달 닫기 여부 결정에 사용)
  const handleChangeExamProblems = async (presetId) => {
    if (!window.confirm('기존 시험의 결과가 삭제됩니다. 계속하시겠습니까?')) return false;

    try {
      const token = await getAccessToken();

      // 기존 시험 삭제
      const deleteRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp_id}/${currentExam.exam_info.exam_id}`,
        { method: 'DELETE', headers: { 'Authorization': token } }
      );
      if (!deleteRes.ok) {
        if (deleteRes.status === 423) alert('확정된 시험은 삭제할 수 없습니다.');
        else alert('시험 삭제 중 오류가 발생했습니다.');
        return false;
      }

      // 새 시험 생성 (preset 기반)
      const createRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp_id}/preset`,
        {
          method: 'POST',
          headers: { 'Authorization': token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ preset_id: presetId })
        }
      );
      if (!createRes.ok) {
        let errorMsg = `시험 생성 실패 (${createRes.status})`;
        try {
          const errorData = await createRes.json();
          errorMsg = errorData.message || errorMsg;
        } catch {}
        throw new Error(errorMsg);
      }

      alert('시험지가 변경되었습니다.');
      fetchExamList();
      return true;
    } catch (error) {
      console.error('시험지 출제변경 오류:', error);
      alert(`시험지 출제변경 중 오류가 발생했습니다: ${error.message}`);
      return false;
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const handleDeleteExam = async () => {
    if (!window.confirm('시험을 삭제하시겠습니까?')) return;

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp_id}/${currentExam.exam_info.exam_id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': token }
        }
      );

      if (!response.ok) {
        if (response.status === 423) {
          alert('확정된 시험은 삭제할 수 없습니다.');
        } else if (response.status === 409) {
          alert('잘못된 시험 정보입니다.');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return;
      }

      alert('시험이 삭제되었습니다.');
      fetchExamList(); // 시험 목록 새로고침
    } catch (error) {
      console.error('시험 삭제 오류:', error);
      alert('시험 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleReOpenExam = async () => {
    if (!window.confirm('시험의 결과를 취소 하시겠습니까? 결과를 다시 입력할 수 있게 됩니다.')) return;

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp_id}/${currentExam.exam_info.exam_id}/re-open`,
        {
          method: 'POST',
          headers: { 'Authorization': token }
        }
      );

      if (!response.ok) {
        if (response.status === 423) {
          alert('이미 결과취소 된 상태입니다.');
        } else if (response.status === 403) {
          alert('권한이 없습니다.');
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return;
      }

      const data = await response.json();
      alert('시험의 결과가 취소 되었습니다.');

      // 이전 시험이 있는 경우 해당 시험의 결과 맵 호출
      if (data.data.prev_exam) {
        fetchResultMap(data.data.prev_exam, null, examList);
      }

      fetchExamDetail(currentExam.exam_info.exam_id);
    } catch (error) {
      console.error('시험 결과취소 오류:', error);
      alert('시험 결과취소 중 오류가 발생했습니다.');
    }
  };

  const fetchResultMap = async (examId, currentExamId = null, examListData = null) => {
    if (!examId) return;

    try {
      setLoading(true);
      const token = await getAccessToken();
      
      // currentExamId가 있으면 현재 시험의 문제 좌표를 가져옴
      let currentExamProblems = null;
      if (currentExamId && currentExamId !== examId) {
        try {
          const currentExamResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp_id}/${currentExamId}`,
            { headers: { 'Authorization': token } }
          );
          if (currentExamResponse.ok) {
            const currentExamData = await currentExamResponse.json();
            currentExamProblems = currentExamData.data.exam_problems;
            // console.log('현재 시험 문제 좌표:', currentExamProblems);
          }
        } catch (error) {
          console.error('현재 시험 문제 정보 조회 실패:', error);
        }
      }
      
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/resultmap?exam_id=${examId}`;
      // console.log('Fetching result map from:', url); // 디버깅용
      
      const response = await fetch(url, {
        headers: { 'Authorization': token }
      });

      // 422는 결과가 없는 시험 - 정상적인 응답이므로 에러가 아님
      if (response.status === 422) {
        // console.info('[ExamResult] 결과 입력이 안된 시험입니다 (422는 정상). 이전 확정 시험의 결과맵을 찾습니다.');
        
        // examListData가 있으면 이전 확정 시험 찾기
        const listToUse = examListData || examList;
        if (listToUse && listToUse.length > 0) {
          // 현재 시험 정보 찾기
          const currentExamInfo = listToUse.find(exam => exam.exam_id === examId);
          
          if (currentExamInfo) {
            // 이전 확정 시험 찾기
            const previousConfirmedExam = listToUse
              .filter(exam =>
                exam.exam_status === 999 &&
                new Date(exam.exam_created_at) < new Date(currentExamInfo.exam_created_at)
              )
              .sort((a, b) => new Date(b.exam_created_at) - new Date(a.exam_created_at))[0];
            
            if (previousConfirmedExam) {
              // console.info('[ExamResult] 이전 확정 시험 찾음:', previousConfirmedExam.exam_id);
              // 이전 확정 시험의 결과맵을 재귀적으로 호출
              return fetchResultMap(previousConfirmedExam.exam_id, examId, examListData);
            }
          }
        }
        
        // 이전 시험을 찾을 수 없으면 빈 결과맵 표시
        // console.info('[ExamResult] 이전 확정 시험을 찾을 수 없습니다. 빈 결과맵을 표시합니다.');
        setTreeData(null);
        setAnalysis({
          target_grade: 0,
          progress: 0.0,
          percentage: 0.0,
          remain_time: 0
        });
        setError('');
        return;
      }

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      
      // currentExamProblems가 있으면 결과맵에 현재 시험 문제 표시 추가
      if (currentExamProblems && data.data) {
        data.data.currentExamProblems = currentExamProblems;
        // console.log('현재 시험 문제를 결과맵에 추가:', currentExamProblems.length, '개');
      }
      
      // currentExamId가 있으면 현재 시험 ID 정보 추가 (결과가 없는 시험인 경우)
      if (currentExamId && data.data) {
        data.data.current_exam_id = currentExamId;
      }
      
      setTreeData(data.data);
      setAnalysis(data.data.analysis || {
        target_grade: 0,
        progress: 0.0,
        percentage: 0.04,
        remain_time: 42240
      });
      setError('');
    } catch (error) {
      console.error('시험 결과 맵 불러오기 실패:', error);
      setError('결과과 맵을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const calculateGradeProgress = () => {
    const targetGrade = analysis?.target_grade;
    const progress = analysis?.progress || 0;

    switch (targetGrade) {
      case 0: return 0.8 + 0.2 * progress;
      case 1: return 0.6 + 0.2 * progress;
      case 2: return 0.4 + 0.2 * progress;
      case 3: return 0.4 * progress;
      default: return progress;
    }
  };

  const getParentInfo = (path, depth, item) => {
    // 원본 이름들을 보존
    const originalNames = {
      d1: '',
      d2: '',
      d3: '',
      current: item[1]
    };

    // 경로 처리를 위한 임시 문자열
    const tempPath = path.replace(/\([^)]*\)/g, '');

    const pathParts = tempPath.match(/\d+/g) || [];

    const address = pathParts.join('/');

    const depthData = {
      'd1': treeData.field_info.d1_y_axis || [],
      'd2': treeData.field_info.d2_y_axis || [],
      'd3': treeData.field_info.d3_y_axis || [],
      'd4': treeData.field_info.d4_y_axis || []
    };

    // 각 depth 항목 찾기
    const d1Item = depthData.d1.find(item => {
      const cleanPath = item[2].replace(/\([^)]*\)/g, '');
      const match = cleanPath.includes(`${pathParts[0]}/${pathParts[1]}`);
      if (match) originalNames.d1 = item[1];
      return match;
    });

    const d2Item = depth !== 'd1' ? depthData.d2.find(item => {
      const cleanPath = item[2].replace(/\([^)]*\)/g, '');
      const match = cleanPath.includes(`${pathParts[0]}/${pathParts[1]}/${pathParts[2]}`);
      if (match) originalNames.d2 = item[1];
      return match;
    }) : null;

    const d3Item = (depth === 'd4' || depth === 'd3') ? depthData.d3.find(item => {
      const cleanPath = item[2].replace(/\([^)]*\)/g, '');
      const match = cleanPath.includes(`${pathParts[0]}/${pathParts[1]}/${pathParts[2]}/${pathParts[3]}`);
      if (match) originalNames.d3 = item[1];
      return match;
    }) : null;

    // 경로 정보만 추출
    const d1Parts = d1Item ? d1Item[2].match(/\d+/g) || [] : [];
    const d2Parts = d2Item ? d2Item[2].match(/\d+/g) || [] : [];
    const d3Parts = d3Item ? d3Item[2].match(/\d+/g) || [] : [];
    const currentParts = item[2].match(/\d+/g) || [];

    const paths = {
      d1: d1Parts.length >= 2 ? `(${d1Parts.slice(0, 2).join('_')})` : '',
      d2: d2Parts.length >= 3 ? `(${d2Parts.slice(0, 3).join('_')})` : '',
      d3: d3Parts.length >= 4 ? `(${d3Parts.slice(0, 4).join('_')})` : '',
      d4: currentParts.length >= 5 ? `(${currentParts.slice(0, 5).join('_')})` : ''
    };

    // HTML 특수문자 디코드 함수
    const decodeHtml = (html) => {
      const txt = document.createElement('textarea');
      txt.innerHTML = html;
      return txt.value;
    };

    const result = {
      d1Name: d1Item ? decodeHtml(originalNames.d1) + ` <span class="text-primary">${paths.d1}</span>` : '',
      d2Name: d2Item ? decodeHtml(originalNames.d2) + ` <span class="text-primary">${paths.d2}</span>` : '',
      d3Name: d3Item ? decodeHtml(originalNames.d3) + ` <span class="text-primary">${paths.d3}</span>` : '',
      name: decodeHtml(originalNames.current) + ` <span class="text-primary">${depth === 'd1' ? paths.d1 :
          depth === 'd2' ? paths.d2 :
            depth === 'd3' ? paths.d3 :
              paths.d4
        }</span>`,
      address: address
    };

    return result;
  };

  const getLevelData = () => {
    if (!treeData) return [];

    const depthData = {
      'd1': treeData.field_info.d1_y_axis || [],
      'd2': treeData.field_info.d2_y_axis || [],
      'd3': treeData.field_info.d3_y_axis || [],
      'd4': treeData.field_info.d4_y_axis || []
    };

    const currentDepthData = depthData[selectedDepth].map(item => {
      const parentInfo = getParentInfo(item[2], selectedDepth, item);
      return {
        ...parentInfo,
        status: item[3],
        originalPath: item[2]
      };
    });

    return currentDepthData;
  };

  const getVisibleColumns = () => {
    switch (selectedDepth) {
      case 'd4':
        return ['d1Name', 'd2Name', 'd3Name', 'name'];
      case 'd3':
        return ['d1Name', 'd2Name', 'name'];
      case 'd2':
        return ['d1Name', 'name'];
      case 'd1':
        return ['name'];
      default:
        return ['name'];
    }
  };

  const columnTitles = {
    'd1Name': '대단원',
    'd2Name': '소단원',
    'd3Name': '대유형',
    'name': depthTitles[selectedDepth]
  };

  const renderStatusBox = (status, address, levelIndex) => {
    if (!address) return null;

    const normalizedAddress = address.replace(/_/g, '/');
    const problemCount = isCreateExamModalOpen ?
      problemCounts[normalizedAddress]?.[levelIndex] || 0 :
      null;

    const isDeltaSelected = isCreateExamModalOpen &&
      examType === 'delta' &&
      deltaSelections.includes(`${normalizedAddress},${levelIndex + 1}`);

    // 결과 맵 생성
    const resultMap = new Map(
      currentExam?.result?.map(item => [item.problem_id, item.result]) || []
    );

    // 현재 시험의 모든 문제 번호를 먼저 계산
    const allProblemNumbers = {};
    currentExam?.problems_list?.forEach((problem, index) => {
      const key = `${problem.address},${problem.step}`;
      if (!allProblemNumbers[key]) {
        allProblemNumbers[key] = [];
      }
      
      allProblemNumbers[key].push({
        number: index + 1,
        problemId: problem.problem_id,
        // resultMap에서 직접 결과 조회
        isCorrect: resultMap.get(problem.problem_id) ?? null
      });
    });

    // currentExamProblems가 있는 경우 (이전 시험의 결과맵 + 현재 시험 문제 표시)
    const isCurrentExamProblem = treeData?.currentExamProblems?.some(problem => {
      const match = problem.address === normalizedAddress && problem.step === levelIndex + 1;
      if (match) {
        // console.log('현재 시험 문제 매치:', normalizedAddress, 'level:', levelIndex + 1);
      }
      return match;
    });

    // 현재 칸의 문제 정보 가져오기
    const currentKey = `${normalizedAddress},${levelIndex + 1}`;
    const problemInfos = allProblemNumbers[currentKey];
    const isCurrentProblem = !!problemInfos?.length;

    const handleBoxClick = (e) => {
      e.stopPropagation();
      if (isCurrentProblem && !isCreateExamModalOpen) {
        setPreviewInfo({
          isOpen: true,
          problemId: problemInfos.map(info => info.problemId),
          problemNumber: problemInfos.map(info => info.number),
          isCorrect: problemInfos.map(info => resultMap.get(info.problemId) ?? null)
        });
      } else if (isCreateExamModalOpen && examType === 'delta') {
        handleDeltaSelection(normalizedAddress, levelIndex + 1);
      }
    };

    return (
      <div className="relative">
        <div
          onClick={handleBoxClick}
          className={`
            size-6 rounded-3xl 
            ${colors[status]?.color || 'bg-gray-200'}
            ${isDeltaSelected ? 'ring-2 ring-primary ring-offset-2' : ''}
            ${isCurrentProblem ? 'cursor-pointer ring-2 ring-black' : ''}
            flex items-center justify-center
            text-[10px] font-semibold
            ${status === 0 ? 'text-black' : 'text-white'}
            relative
            ${isCreateExamModalOpen && examType === 'delta' ? 'cursor-pointer hover:opacity-80' : ''}
          `}
        >
          {isCreateExamModalOpen ?
            (problemCount > 0 ? problemCount : '') :
            (isCurrentExamProblem ? 
              <span className="font-bold text-black">●</span> 
              : (isCurrentProblem ? 
                <span className="font-bold text-black">
                  {problemInfos.map(info => info.number).join(',')}
                </span> 
                : (status === 0 ? '×' : '')))}
        </div>
      </div>
    );
  };

  const getGroupedData = () => {
    const data = getLevelData();
    
    // 행 고정 모드이고 고정된 행이 있는 경우
    if (isRowFixMode && fixedRows.length > 0) {
      return data.filter(item => 
        fixedRows.some(fixedRow => fixedRow.originalPath === item.originalPath)
      );
    }

    const groupedRows = [];
    let currentGroup = null;

    // 현재 시험의 문제 주소들을 Set으로 저장
    const problemAddresses = new Set(currentExam?.problems_list?.map(p => p.address));

    data.forEach(item => {
      const groupKey = selectedDepth === 'd4'
        ? `${item.d1Name}-${item.d2Name}-${item.d3Name}`
        : selectedDepth === 'd3'
          ? `${item.d1Name}-${item.d2Name}`
          : selectedDepth === 'd2'
            ? `${item.d1Name}`
            : null;

      // originalPath를 사용하여 주소 확인
      const address = item.originalPath;

      // showOnlyProblems가 true일 때는 문제가 있는 행만 포함
      if (!showOnlyProblems || problemAddresses.has(address)) {
        if (groupKey !== currentGroup) {
          currentGroup = groupKey;
          groupedRows.push({
            type: 'group',
            ...item,
            isFirstInGroup: true
          });
        } else {
          groupedRows.push({
            type: 'item',
            ...item,
            isFirstInGroup: false
          });
        }
      }
    });

    return groupedRows;
  };

  const handleShareResult = async (examId) => {
    // 테스트 결과가 없는 경우 체크
    if (!currentExam?.exam_info?.total_problems || currentExam.exam_info.total_problems === 0) {
      alert('테스트 결과가 없습니다. 테스트를 먼저 진행해주세요.');
      return;
    }

    // 테스트가 완료되지 않은 경우 체크
    if (currentExam.exam_info.exam_status !== 999) {
      alert('테스트가 아직 완료되지 않았습니다. 테스트를 완료한 후 결과를 공유해주세요.');
      return;
    }

    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/resultmap/issue`, {
        method: 'POST',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ exam_id: examId })
      });

      if (response.ok) {
        const { data } = await response.json();
        const shareUrl = `${process.env.REACT_APP_BASE_URL}/result/${data}`;

        await navigator.clipboard.writeText(shareUrl);
        alert('결과 공유 링크가 클립보드에 복사되었습니다.');
      }
    } catch (error) {
      console.error('결과 공유 링크 생성 실패:', error);
      alert('결과 공유 링크 생성에 실패했습니다.');
    }
  };

  const handleOpenResultModal = (isConfirm = false) => {
    if (currentExam?.exam_info?.exam_id) {
      setSelectedExamId(currentExam.exam_info.exam_id);
      setIsConfirming(isConfirm);
      setIsResultModalOpen(true);
    }
  };

  const getDepthFromAddress = (address) => {
    const parts = address.split('/');
    switch (parts.length) {
      case 2: return 'd1';
      case 3: return 'd2';
      case 4: return 'd3';
      case 5: return 'd4';
      default: return null;
    }
  };

  const isAddressSelected = (address) => {
    if (!address) return false;

    // originalPath를 사용하도록 수정
    const normalizedAddress = address.originalPath || address;

    // 직접 선택된 경우
    if (selectedAddressesForExam.includes(normalizedAddress)) return true;

    // 상위 주소에 의해 선택된 경우
    return selectedAddressesForExam.some(selectedAddr =>
      normalizedAddress.startsWith(selectedAddr + '/'));
  };

  const handleTableRowClick = (item) => {
    if (!isCreateExamModalOpen || !(examType === 'beta' || examType === 'gamma')) return;

    // item.originalPath를 사용하여 정확한 경로 가져오기
    const address = item.originalPath;

    if (!address) return;

    const depth = getDepthFromAddress(address);
    if (!depth) return;

    // 현재 주소의 상위 주소들 확인
    const addressParts = address.split('/');
    const parentAddresses = [];
    for (let i = 2; i <= addressParts.length; i++) {
      parentAddresses.push(addressParts.slice(0, i).join('/'));
    }

    // 현재 주소가 선택되어 있는지 확인
    const isCurrentlySelected = selectedAddressesForExam.includes(address);

    // 상위 주소가 선택되어 있는지 확인
    const selectedParent = parentAddresses.find(parentAddr =>
      selectedAddressesForExam.includes(parentAddr)
    );

    setHierarchicalSelections(prev => {
      const newSelections = { ...prev };

      if (selectedParent) {
        // 상위 주소가 선택된 경우, 해당 상위 주소의 선택을 해제하고
        // 모든 하위 주소를 선택 (현재 주소 제외)
        const parentDepth = getDepthFromAddress(selectedParent);
        newSelections[parentDepth].delete(selectedParent);

        // 하위 주소들 선택
        const siblings = getChildAddresses(selectedParent, depth);
        siblings.forEach(sibling => {
          if (sibling !== address) {
            newSelections[depth].add(sibling);
          }
        });
      } else if (isCurrentlySelected) {
        // 선택 해제
        newSelections[depth].delete(address);
      } else {
        // 새로운 선택
        newSelections[depth].add(address);
      }

      return newSelections;
    });

    // selectedAddressesForExam 업데이트
    if (selectedParent) {
      const siblings = Array.from(getChildAddresses(selectedParent, depth));
      setSelectedAddressesForExam(prev => {
        const newSelection = prev.filter(addr => !addr.startsWith(selectedParent));
        return [...newSelection, ...siblings.filter(sibling => sibling !== address)];
      });
    } else if (isCurrentlySelected) {
      setSelectedAddressesForExam(prev =>
        prev.filter(addr => addr !== address && !addr.startsWith(address + '/'))
      );
    } else {
      setSelectedAddressesForExam(prev => {
        const filtered = prev.filter(addr =>
          !addr.startsWith(address + '/') &&
          !address.startsWith(addr + '/')
        );
        return [...filtered, address];
      });
    }
  };

  const getChildAddresses = (parentAddress, depth) => {
    const depthData = {
      'd1': treeData?.field_info?.d1_y_axis || [],
      'd2': treeData?.field_info?.d2_y_axis || [],
      'd3': treeData?.field_info?.d3_y_axis || [],
      'd4': treeData?.field_info?.d4_y_axis || []
    };

    const children = new Set();
    const parentParts = parentAddress.split('/');

    depthData[depth]?.forEach(item => {
      const itemAddress = item[2].replace(/_/g, '/');
      const itemParts = itemAddress.split('/');

      if (itemParts.slice(0, parentParts.length).join('/') === parentAddress) {
        children.add(itemAddress);
      }
    });

    return children;
  };

  const optimizeSelections = () => {
    const newSelection = new Set(selectedAddressesForExam);
    let hasChanges = false;

    // 각 depth별로 확인
    ['d3', 'd2', 'd1'].forEach(depth => {
      const addresses = Array.from(newSelection);
      const groupedByParent = {};

      // 부모 주소별로 그룹화
      addresses.forEach(addr => {
        const addrDepth = getDepthFromAddress(addr);
        if (addrDepth === getNextDepth(depth)) {
          const parentAddr = addr.split('/').slice(0, -1).join('/');
          groupedByParent[parentAddr] = groupedByParent[parentAddr] || new Set();
          groupedByParent[parentAddr].add(addr);
        }
      });

      // 각 부모 주소에 대해 모든 자식이 선택되었는지 확인
      Object.entries(groupedByParent).forEach(([parentAddr, children]) => {
        const allChildren = getChildAddresses(parentAddr, getNextDepth(depth));
        if (allChildren.size === children.size) {
          // 모든 자식이 선택되었다면 자식들을 제거하고 부모를 추가
          children.forEach(child => newSelection.delete(child));
          newSelection.add(parentAddr);
          hasChanges = true;
        }
      });
    });

    if (hasChanges) {
      setSelectedAddressesForExam(Array.from(newSelection));

      // hierarchicalSelections도 업데이트
      setHierarchicalSelections(prev => {
        const newSelections = { ...prev };
        ['d1', 'd2', 'd3', 'd4'].forEach(d => {
          newSelections[d] = new Set(
            Array.from(newSelection).filter(addr =>
              getDepthFromAddress(addr) === d
            )
          );
        });
        return newSelections;
      });
    }
  };

  const getNextDepth = (currentDepth) => {
    const depths = ['d1', 'd2', 'd3', 'd4'];
    const currentIndex = depths.indexOf(currentDepth);
    return depths[currentIndex + 1] || currentDepth;
  };

  const getPreviousDepth = (currentDepth) => {
    const depths = ['d1', 'd2', 'd3', 'd4'];
    const currentIndex = depths.indexOf(currentDepth);
    return depths[currentIndex - 1] || currentDepth;
  };

  const handlePrintExam = () => {
    setShowPrintPreview(true);
  };


  const fetchProblemCounts = async (treeId, signal) => {
    if (!treeId) return;

    try {
      const token = await getAccessToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/referencefields/${treeId}/count/problems`,
        {
          headers: { 'Authorization': token },
          signal
        }
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      const counts = {};

      Object.entries(data.data.field_problems).forEach(([depthKey, depthData]) => {
        Object.entries(depthData).forEach(([address, levelCounts]) => {
          counts[address] = levelCounts;
        });
      });

      setProblemCounts(counts);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('문제 갯수 조회 오류:', error);
    }
  };

  useEffect(() => {
    if (treeData?.field_info?.d1_y_axis?.[0]?.[2] && isCreateExamModalOpen) {
      const treeId = treeData.field_info.d1_y_axis[0][2].split('/')[0];
      const controller = new AbortController();
      fetchProblemCounts(treeId, controller.signal);
      return () => controller.abort();
    }
  }, [treeData?.field_info?.d1_y_axis?.[0]?.[2], isCreateExamModalOpen]);

  const handleDeltaSelection = (address, level) => {
    const selectionKey = `${address},${level}`;
    setDeltaSelections(prev => {
      const exists = prev.find(item => item === selectionKey);
      if (exists) {
        return prev.filter(item => item !== selectionKey);
      }
      return [...prev, selectionKey];
    });
  };

  const renderTable = () => {
    const groupedData = getGroupedData();

    return (
      <div className="md:block">
        {/* 데스크톱 뷰 - 기존과 동일 */}
        <table className="mb-[400px] w-full text-sm hidden md:table border-t border-b border-gray-200">
          <thead>
            <tr>
              {getVisibleColumns().map(column => (
                <th key={column} className="px-2 py-1 text-left">
                  <span className="text-sm text-gray-900">{columnTitles[column]}</span>
                </th>
              ))}
              {[1, 2, 3, 4, 5].map(level => (
                <th key={level} className="w-8 p-1 text-center">
                  <span className="text-gray-600">
                    <span className="text-xs">Lv</span>
                    <span className="text-sm ml-0.5">{level}</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groupedData.map((item, index) => {
              const pathMatch = item.name?.match(/\(([\d_]+)\)/);
              const address = pathMatch ? pathMatch[1].split('_').join('/') : null;

              return (
                <tr
                  key={index}
                  onClick={() => {
                    if (isSelectingRows) {
                      handleRowSelection(item);
                    } else {
                      handleTableRowClick(item);
                    }
                  }}
                  className={`
                    ${!item.isFirstInGroup ? 'border-t-0' : ''}
                    relative transition-all duration-150 
                    hover:z-10 hover:outline hover:outline-1 hover:outline-primary
                    ${isCreateExamModalOpen && (examType === 'beta' || examType === 'gamma') ? 'cursor-pointer' : ''}
                    ${isAddressSelected(item) ? 'bg-green-50' : ''}
                    ${isSelectingRows && selectedRows.some(row => row.originalPath === item.originalPath) ? 'bg-blue-100' : ''}
                    ${isSelectingRows ? 'cursor-pointer' : ''}
                  `}
                >
                  {getVisibleColumns().map(column => {
                    const content = item.isFirstInGroup || column === 'name' ? item[column] : '';
                    if (!content) return <td key={column} className="px-2 py-1 text-left text-sm" />;

                    // HTML 태그를 포함하는 부분과 일반 텍스트 부분을 분리
                    const [textPart, spanPart] = content.split(/<span/);

                    return (
                      <td key={column} className="px-2 py-1 text-left text-sm">
                        {textPart}
                        {spanPart && (
                          <span
                            className="text-primary"
                            dangerouslySetInnerHTML={{
                              __html: spanPart.replace(/^[^>]*>/, '').replace(/<\/span>$/, '')
                            }}
                          />
                        )}
                      </td>
                    );
                  })}
                  {item.status.map((status, statusIndex) => (
                    <td key={statusIndex} className="p-1 text-center">
                      <div className="flex justify-center">
                        {renderStatusBox(
                          status,
                          item.originalPath,
                          statusIndex
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* 모바일 뷰 - 고정 레벨 컬럼 */}
        <div className="md:hidden mb-[400px] relative border-t border-b border-gray-200">
          <div className="flex">
            {/* 스크롤 가능한 좌측 영역 */}
            <div ref={mobileScrollRef} className="flex-1 overflow-x-auto">
              <table className="text-sm border-collapse">
                <thead>
                  <tr className="h-[36px]">
                    {getVisibleColumns().map(column => (
                      <th key={column} className="px-2 text-left whitespace-nowrap h-[36px] align-middle">
                        <span className="text-sm text-gray-900">{columnTitles[column]}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedData.map((item, index) => {
                    const pathMatch = item.name?.match(/\(([\d_]+)\)/);
                    const address = pathMatch ? pathMatch[1].split('_').join('/') : null;

                    return (
                      <tr
                        key={index}
                        onClick={() => {
                          if (isSelectingRows) {
                            handleRowSelection(item);
                          } else {
                            handleTableRowClick(item);
                          }
                        }}
                        className={`
                          h-[40px]
                          ${!item.isFirstInGroup ? 'border-t-0' : ''}
                          relative transition-all duration-150 
                          hover:z-10 hover:outline hover:outline-1 hover:outline-primary
                          ${isCreateExamModalOpen && (examType === 'beta' || examType === 'gamma') ? 'cursor-pointer' : ''}
                          ${isAddressSelected(item) ? 'bg-green-50' : ''}
                          ${isSelectingRows && selectedRows.some(row => row.originalPath === item.originalPath) ? 'bg-blue-100' : ''}
                          ${isSelectingRows ? 'cursor-pointer' : ''}
                        `}
                      >
                        {getVisibleColumns().map(column => {
                          const content = item.isFirstInGroup || column === 'name' ? item[column] : '';
                          if (!content) return <td key={column} className="px-2 py-1 text-left text-sm whitespace-nowrap h-[40px]" />;

                          // HTML 태그를 포함하는 부분과 일반 텍스트 부분을 분리
                          const [textPart, spanPart] = content.split(/<span/);

                          return (
                            <td key={column} className="px-2 py-1 text-left text-sm whitespace-nowrap h-[40px]">
                              {textPart}
                              {spanPart && (
                                <span
                                  className="text-primary"
                                  dangerouslySetInnerHTML={{
                                    __html: spanPart.replace(/^[^>]*>/, '').replace(/<\/span>$/, '')
                                  }}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 고정된 우측 레벨 영역 */}
            <div className="flex-shrink-0 bg-white shadow-[-2px_0_4px_rgba(0,0,0,0.1)]">
              <table className="text-sm border-collapse">
                <thead>
                  <tr className="h-[36px]">
                    {[1, 2, 3, 4, 5].map(level => (
                      <th key={level} className="w-8 text-center bg-white h-[36px] align-middle">
                        <span className="text-gray-600">
                          <span className="text-xs">Lv</span>
                          <span className="text-sm ml-0.5">{level}</span>
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedData.map((item, index) => (
                    <tr
                      key={index}
                      className={`
                        h-[40px]
                        ${!item.isFirstInGroup ? 'border-t-0' : ''}
                        ${isAddressSelected(item) ? 'bg-green-50' : ''}
                        ${isSelectingRows && selectedRows.some(row => row.originalPath === item.originalPath) ? 'bg-blue-100' : ''}
                      `}
                    >
                      {item.status.map((status, statusIndex) => (
                        <td key={statusIndex} className="p-1 text-center bg-white h-[40px]">
                          <div className="flex justify-center items-center h-full">
                            {renderStatusBox(
                              status,
                              item.originalPath,
                              statusIndex
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // isAllChildrenSelected 유틸리티 함수 추가
  const isAllChildrenSelected = (parentAddress, depth) => {
    if (!parentAddress || !depth) return false;

    const nextDepth = getNextDepth(depth);
    const children = getChildAddresses(parentAddress, nextDepth);

    return Array.from(children).every(child =>
      hierarchicalSelections[nextDepth].has(child)
    );
  };

  // 칩 렌더링을 위한 함수 수정
  const renderChips = () => {
    return selectedAddressesForExam.map((address, idx) => (
      <span
        key={idx}
        className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1"
      >
        <span className="max-w-[200px] truncate">{address}</span>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveAddress(address);
          }}
          className="ml-1 h-auto min-h-0 p-0 text-gray-500 hover:text-gray-700"
        >
          ×
        </Button>
      </span>
    ));
  };

  // 자동 병합을 위한 useEffect 추가
  useEffect(() => {
    const optimizeSelections = () => {
      const newSelection = new Set(selectedAddressesForExam);
      let hasChanges = false;

      // 각 depth별로 확인
      ['d3', 'd2', 'd1'].forEach(depth => {
        const addresses = Array.from(newSelection);
        const groupedByParent = {};

        // 부모 주소별로 그룹화
        addresses.forEach(addr => {
          const addrDepth = getDepthFromAddress(addr);
          if (addrDepth === getNextDepth(depth)) {
            const parentAddr = addr.split('/').slice(0, -1).join('/');
            groupedByParent[parentAddr] = groupedByParent[parentAddr] || new Set();
            groupedByParent[parentAddr].add(addr);
          }
        });

        // 각 부모 주소에 대해 모든 자식이 선택되었는지 확인
        Object.entries(groupedByParent).forEach(([parentAddr, children]) => {
          const allChildren = getChildAddresses(parentAddr, getNextDepth(depth));
          if (allChildren.size === children.size) {
            // 모든 자식이 선택되었다면 자식들을 제거하고 부모를 추가
            children.forEach(child => newSelection.delete(child));
            newSelection.add(parentAddr);
            hasChanges = true;
          }
        });
      });

      if (hasChanges) {
        setSelectedAddressesForExam(Array.from(newSelection));

        // hierarchicalSelections도 업데이트
        setHierarchicalSelections(prev => {
          const newSelections = { ...prev };
          ['d1', 'd2', 'd3', 'd4'].forEach(d => {
            newSelections[d] = new Set(
              Array.from(newSelection).filter(addr =>
                getDepthFromAddress(addr) === d
              )
            );
          });
          return newSelections;
        });
      }
    };

    optimizeSelections();
  }, [selectedAddressesForExam]);

  // handleRemoveAddress 함수 추가
  const handleRemoveAddress = (addressToRemove) => {
    // hierarchicalSelections 업데이트
    setHierarchicalSelections(prev => {
      const newSelections = { ...prev };
      const depth = getDepthFromAddress(addressToRemove);

      if (depth) {
        newSelections[depth].delete(addressToRemove);
        // 하위 주소들도 모두 제거
        Object.keys(newSelections).forEach(d => {
          if (d > depth) {
            newSelections[d] = new Set(
              Array.from(newSelections[d]).filter(addr =>
                !addr.startsWith(addressToRemove + '/')
              )
            );
          }
        });
      }

      return newSelections;
    });

    // selectedAddressesForExam 업데이트
    setSelectedAddressesForExam(prev =>
      prev.filter(addr =>
        addr !== addressToRemove && !addr.startsWith(addressToRemove + '/')
      )
    );
  };

  // CreateExamModal이 닫힐 때 problemCounts 초기화
  const handleCloseCreateExamModal = () => {
    setIsCreateExamModalOpen(false);
    setSelectedAddressesForExam([]);
    setDeltaSelections([]);
    setExamType('');
    setHierarchicalSelections({
      d1: new Set(),
      d2: new Set(),
      d3: new Set(),
      d4: new Set()
    });
    setProblemCounts({}); // problemCounts 초기화 추가
  };

  // CreateExamModal 열릴 때 문제 행만 보기 off
  const handleCreateExamModalOpen = () => {
    // 버튼 위치 업데이트
    if (floatingButtonRef.current) {
      const rect = floatingButtonRef.current.getBoundingClientRect();
      setFloatingButtonRect(rect);
    }
    setIsCreateExamModalOpen(true);
    setShowOnlyProblems(false); // 문제 행만 보기 off
    setIsRowFixMode(false); // 행 고정 모드 off
    setFixedRows([]); // 고정된 행 초기화
    setSelectedRows([]); // 선택된 행 초기화
    setIsSelectingRows(false); // 선택 모드 off
  };

  // 새로운 state 추가
  const [isRowFixMode, setIsRowFixMode] = useState(false);
  const [fixedRows, setFixedRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectingRows, setIsSelectingRows] = useState(false);

  // 행 선택 처리 함수 추가
  const handleRowSelection = (item) => {
    if (!isSelectingRows) return;
    
    setSelectedRows(prev => {
      const itemKey = item.originalPath;
      if (prev.some(row => row.originalPath === itemKey)) {
        return prev.filter(row => row.originalPath !== itemKey);
      }
      return [...prev, item];
    });
  };

  // 행 고정 완료 처리 함수
  const handleFixRows = () => {
    if (selectedRows.length === 0) {
      alert('선택된 행이 없습니다.');
      return;
    }
    
    if (window.confirm('선택한 행을 고정하시겠습니까?')) {
      setFixedRows(selectedRows);
      setSelectedRows([]);
      setIsSelectingRows(false);
      setIsRowFixMode(true);
      // 출제 문제만 보기가 켜져있으면 끄기
      if (showOnlyProblems) {
        setShowOnlyProblems(false);
      }
      
      // 행 고정 완료 시 모바일에서 중유형 컬럼으로 스크롤
      if (mobileScrollRef.current && window.innerWidth < 768 && selectedDepth === 'd4') {
        setTimeout(() => {
          if (mobileScrollRef.current) {
            const table = mobileScrollRef.current.querySelector('table');
            if (table) {
              const headers = table.querySelectorAll('thead th');
              if (headers.length > 0) {
                const lastHeader = headers[headers.length - 1];
                mobileScrollRef.current.scrollLeft = lastHeader.offsetLeft;
              }
            }
          }
        }, 100);
      }
    }
  };

  // 행 고정 해제 함수
  const handleClearFixedRows = () => {
    setFixedRows([]);
    setIsRowFixMode(false);
  };

  // fetchWithTokenRefresh 함수 추가
  const fetchWithTokenRefresh = async (url, options = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('API 요청 실패');
      }

      return await response.json();
    } catch (error) {
      console.error('API 호출 에러:', error);
      throw error;
    }
  };

  // 데스크톱용 버튼 위치 계산 (사이드바 + p-8 + 버튼크기)
  const desktopButtonLeft = `calc(${isExpanded ? '180px' : '90px'} + 2rem + 56px)`;

  return (
    <>
      {/* 플로팅 버튼들 - 모바일과 데스크톱 구분 */}
      {isMobileView ? (
        // 모바일: 기존 FloatingButton 컴포넌트 그대로 사용
        <>
          <FloatingButton
            ref={floatingButtonRef}
            variant="plus"
            title="컬럼 추가"
            onClick={() => {
              if (isCreateExamModalOpen) {
                handleCloseCreateExamModal();
              } else {
                handleCreateExamModalOpen();
              }
            }}
            usePortal={true}
          />
          <FloatingButton
            variant="prev"
            title={selectedExamIndex !== examList.length - 1 ? '이전 시험' : '이전 시험 없음'}
            onClick={() => handleExamChange('prev')}
            disabled={selectedExamIndex === examList.length - 1}
            usePortal={true}
          />
          <FloatingButton
            variant="next"
            title={selectedExamIndex !== 0 ? '다음 시험' : '다음 시험 없음'}
            onClick={() => handleExamChange('next')}
            disabled={selectedExamIndex === 0}
            usePortal={true}
          />
        </>
      ) : (
        // 데스크톱: FloatingButton 컴포넌트를 wrapper div 안에서 사용
        createPortal(
          <div style={{ 
            position: 'fixed',
            bottom: '2rem',
            left: desktopButtonLeft,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center', // 세로 가운데 정렬
            gap: '0.5rem',
            transition: 'left 300ms ease-in-out'
          }}>
            <div style={{ position: 'relative', width: '56px', height: '56px' }}>
              <FloatingButton
                ref={floatingButtonRef}
                variant="plus"
                title="컬럼 추가"
                onClick={() => {
                  if (isCreateExamModalOpen) {
                    handleCloseCreateExamModal();
                  } else {
                    handleCreateExamModalOpen();
                  }
                }}
                usePortal={false}
                bottom="0"
                leftOffsetPx={-28}
              />
            </div>
            <div style={{ position: 'relative', width: '48px', height: '48px' }}>
              <FloatingButton
                variant="prev"
                title={selectedExamIndex !== examList.length - 1 ? '이전 시험' : '이전 시험 없음'}
                onClick={() => handleExamChange('prev')}
                disabled={selectedExamIndex === examList.length - 1}
                usePortal={false}
                bottom="0"
                className="!left-0"
              />
            </div>
            <div style={{ position: 'relative', width: '48px', height: '48px' }}>
              <FloatingButton
                variant="next"
                title={selectedExamIndex !== 0 ? '다음 시험' : '다음 시험 없음'}
                onClick={() => handleExamChange('next')}
                disabled={selectedExamIndex === 0}
                usePortal={false}
                bottom="0"
                className="!left-0 !right-auto"
              />
            </div>
          </div>,
          document.body
        )
      )}

      <Layout>
        <div className="relative">
        <div className="w-full">
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12">
                {/* KyTestModal 버튼을 항상 표시 */}
                {!currentExam && !loading && (
                  <div className="rounded-3xl bg-white p-4 shadow mb-6">
                    <div className="text-center">
                      <p className="text-gray-500 mb-4">
                        {error || "테스트 데이터가 없습니다. 테스트를 생성해주세요."}
                      </p>
                      <Button
                        variant="primary"
                        onClick={() => navigate(`/student/${student_id}/kytest`)}
                        icon={<ArrowLeft size={16} />}
                        iconPosition="left"
                      >
                        Ky Test
                      </Button>
                    </div>
                  </div>
                )}
                {!currentExam && loading && createPortal(
                  <LoadingSpinner 
                    message="데이터를 불러오는 중..." 
                    fullScreen={true}
                  />,
                  document.body
                )}
                {currentExam && (
                  <div ref={examCardRef} className="rounded-3xl bg-white p-4 shadow">
                    {/* 헤더 */}
                    <div className="mb-3 flex items-center justify-between">
                      <Button
                        variant="primary"
                        onClick={() => navigate(`/student/${student_id}/kytest`)}
                        icon={<ArrowLeft size={16} />}
                        iconPosition="left"
                      >
                        Ky Test
                      </Button>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          onClick={() => setIsModalOpen(true)}
                          icon={<Menu size={16} className="text-primary" />}
                          iconPosition="left"
                          className="!bg-white !text-primary !border !border-primary hover:!bg-primary/10 "
                        >
                          {/* 테스트 관리 */}
                          테스트 관리
                        </Button>
                      </div>
                    </div>

                    {/* 학생카드 스타일 */}
                    <div 
                      onClick={() => {
                        navigate(`/learning-status/student/${currentExam.student_info.student_id}`);
                      }}
                      className="mb-3 rounded-3xl border border-gray-200 bg-white p-[14.4px] shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-[1.005] hover:border-primary/20 cursor-pointer"
                    >
                      <div className="grid grid-cols-[6fr_4fr] items-center">
                        {/* 좌측 영역 - 학생 정보 */}
                        <div className="flex min-w-0 items-center gap-2 pr-2">
                          <div 
                            className="flex shrink-0 items-center justify-center rounded-full text-white font-bold text-sm bg-gradient-to-r from-primary to-primary/70"
                            style={{ width: '32px', height: '32px' }}
                            title={`학생 ID: ${currentExam.student_info.student_id || '-'}`}
                          >
                            {currentExam.student_info.student_id || '-'}
                          </div>
                          
                          <div className="min-w-0 flex-1 overflow-hidden">
                            <h4 className="truncate text-sm font-medium">{currentExam.student_info.student_name}</h4>
                            <p className="truncate text-xs text-gray-600">{currentExam.student_info.school_name} · {currentExam.student_info.school_grade}학년</p>
                          </div>
                        </div>
                        
                        {/* 우측 영역 - 담당 선생님 & 강의 정보 */}
                        <div className="flex items-center">
                          <div className="flex flex-1 flex-col justify-center text-right">
                            <div className="flex items-center justify-end text-xs">
                              <span className="shrink-0 text-gray-500">담당:</span>
                              <span className="ml-1 truncate font-medium text-gray-700" style={{ maxWidth: '80px' }}>
                                {currentExam.student_info.in_charge_teacher || '-'}
                              </span>
                            </div>
                            <div className="mt-0.5 flex items-center justify-end text-xs">
                              <span className="shrink-0 text-gray-500">강의:</span>
                              <span className="ml-1 truncate font-medium text-gray-700" style={{ maxWidth: '80px' }}>
                                {currentExam.student_info.lecture || currentExam.student_info.class_name || '-'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 테스트 정보 카드 */}
                    <div 
                      onClick={() => {
                        navigate(`/learning-status/student/${currentExam.student_info.student_id}/test/${currentExam.exam_info.exam_id}`);
                      }}
                      className="rounded-3xl border border-gray-200 bg-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:scale-[1.005] hover:border-primary/20 cursor-pointer"
                    >
                      <div className="px-4 pt-4">
                        <div className="mb-2 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {/* 상태 아이콘 */}
                            {getExamStatusIcon(currentExam.exam_info.exam_status)}
                            <div>
                              <h3 className="text-[14px] font-semibold">{currentExam.exam_info.tree_name}</h3>
                              <p className="text-xs text-gray-500">{currentExam.exam_info.edu_ver}</p>
                            </div>
                          </div>
                          <div className="rounded-3xl text-[9px] font-medium text-gray-500">
                            <div>생성: {formatDateTime(currentExam.exam_info.created_at)}</div>
                            <div>수정: {formatDateTime(currentExam.exam_info.updated_at)}</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="px-[14.4px] pb-[14.4px]">
                        {/* 테스트 정보 (4열 그리드) */}
                        <div className="grid grid-cols-4 gap-1 text-xs">
                        <div className="rounded-3xl bg-gray-50 p-2 text-center">
                          <div className="text-gray-500">TID</div>
                          <div className="flex flex-col items-center">
                            <div className="flex items-center justify-center gap-1">
                              <div className="text-sm font-semibold text-gray-800">{currentExam.exam_info.exam_id}</div>
                              <span className="text-sm font-medium text-green-600">
                                {currentExam.exam_info.exam_type === 'alpha' ? 'α' :
                                 currentExam.exam_info.exam_type === 'beta' ? 'β' :
                                 currentExam.exam_info.exam_type === 'gamma' ? 'γ' :
                                 currentExam.exam_info.exam_type === 'delta' ? 'δ' :
                                 currentExam.exam_info.exam_type === 'custom' ? '𝐂' :
                                 currentExam.exam_info.exam_type === 'epsilon' ? 'ε' :
                                 currentExam.exam_info.exam_type || '-'}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">({currentExam.exam_info.exam_count || 0}회)</div>
                          </div>
                        </div>
                        <div className="rounded-3xl bg-gray-50 p-2 text-center">
                          <div className="text-gray-500">난이도</div>
                          <div className="flex flex-col items-center justify-center flex-1">
                            <div className="text-sm font-semibold text-gray-800">
                              {currentExam.exam_info.avg_difficulty || '-'}
                            </div>
                          </div>
                        </div>
                        <div className="rounded-3xl bg-gray-50 p-2 text-center">
                          <div className="text-gray-500">문제수</div>
                          <div className="flex flex-col items-center justify-center flex-1">
                            <div className="text-sm font-semibold text-gray-800">
                              {currentExam.exam_info.total_problems}
                            </div>
                          </div>
                        </div>
                        <div className="rounded-3xl bg-gray-50 p-2 text-center">
                          <div className="text-gray-500">정답률</div>
                          <div className="flex flex-col items-center">
                            <div className="text-sm font-semibold text-green-600">
                              {currentExam.exam_info.exam_status === 999 && currentExam.exam_info.total_problems > 0 ? 
                                `${Math.round((currentExam.exam_info.correct_count / currentExam.exam_info.total_problems) * 100)}%` : 
                                '-'
                              }
                            </div>
                            <div className="text-[8px] font-normal">
                              <span className="text-gray-400">(</span>
                              <span className="text-primary font-bold">{currentExam.exam_info.correct_count || 0}</span>
                              <span className="text-gray-400">/</span>
                              <span className="text-danger font-bold">{currentExam.exam_info.total_problems - (currentExam.exam_info.correct_count || 0)}</span>
                              <span className="text-gray-400">/</span>
                              <span className="text-black font-bold">{currentExam.exam_info.total_problems}</span>
                              <span className="text-gray-400">)</span>
                            </div>
                          </div>
                        </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2 flex justify-between pt-2">
                      <div className="flex space-x-2">
                        {currentExam.exam_info.exam_status === 999 && (
                          <Button
                            variant="warning"
                            onClick={handleReOpenExam}
                            className="!h-[32px] !py-0 flex items-center"
                          >
                            결과취소
                          </Button>
                        )}
                        {!currentExam.exam_info.is_closed && currentExam.exam_info.exam_status === 200 && (
                          <Button
                            variant="primary"
                            onClick={() => handleOpenResultModal(false)}
                            className="!h-[32px] !py-0 flex items-center"
                          >
                            결과입력
                          </Button>
                        )}
                        {!currentExam.exam_info.is_closed && currentExam.exam_info.exam_status === 400 && (
                          <>
                            <Button
                              variant="primary"
                              onClick={() => handleOpenResultModal(false)}
                              className="!h-[32px] !py-0 flex items-center"
                            >
                              결과수정
                            </Button>
                            <Button
                              variant="primary"
                              onClick={() => handleOpenResultModal(true)}
                              className="bg-blue-600 hover:bg-blue-700 !h-[32px] !py-0 flex items-center"
                            >
                              결과확정
                            </Button>
                          </>
                        )}
                        {currentExam.exam_info.exam_status !== 999 && (
                          <Button
                            variant="danger"
                            onClick={handleDeleteExam}
                            className="!h-[32px] !py-0 flex items-center"
                          >
                            테스트 삭제
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {currentExam.exam_info.exam_status === 999 && (
                          <Button
                            onClick={() => setIsReportModalOpen(true)}
                            className={`${getReportStatusInfo(currentExam.report_status || 0).color} ${getReportStatusInfo(currentExam.report_status || 0).textColor} hover:opacity-80 flex items-center gap-1 !h-[32px] !py-0 !border-0 font-medium rounded-3xl focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200`}
                            title={getReportStatusInfo(currentExam.report_status || 0).tooltip}
                          >
                            <FileText size={16} />
                            리포트
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handlePrintExam}
                          className="flex items-center gap-1 !h-[32px] !py-0"
                        >
                          <Printer size={16} />
                          시험지
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="col-span-12">
                {treeData && (
                  <div className="rounded-3xl bg-white p-4 shadow">
                    <div className="mb-4 flex items-center justify-between">
                      <div className="whitespace-nowrap">
                        <h3 className="text-xl font-bold flex items-center gap-2">
                          결과 맵
                          <div className="relative tooltip-container">
                            <HelpCircle
                              size={16}
                              className="text-gray-400 cursor-help hover:text-gray-600"
                              onClick={() => setShowTooltip(!showTooltip)}
                            />
                            {showTooltip && (
                              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 z-50 w-64 rounded-lg bg-white border border-gray-200 p-4 text-sm shadow-lg">
                                <div className="mb-3 font-semibold text-gray-800">결과 맵 설명</div>
                                <div className="space-y-2 text-xs">
                                  <div className="flex items-center gap-2">
                                    <div className="size-3 rounded bg-primary/30"></div>
                                    <span className="text-gray-600">맞을 가능성 높음</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="size-3 rounded bg-primary"></div>
                                    <span className="text-gray-600">맞음</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="size-3 rounded bg-danger/30"></div>
                                    <span className="text-gray-600">틀릴 가능성 높음</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="size-3 rounded bg-danger"></div>
                                    <span className="text-gray-600">틀림</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <div className="size-3 rounded bg-gray-300"></div>
                                    <span className="text-gray-600">문제 없음</span>
                                  </div>
                                </div>
                                <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                                  각 칸을 클릭하면 해당 문제를 미리보기할 수 있습니다.
                                </div>
                              </div>
                            )}
                          </div>
                        </h3>
                        {treeData?.exam_info && (
                          <p className="text-sm text-gray-500">
                            (이전 TID: {treeData.exam_info.exam_id})
                          </p>
                        )}
                      </div>
                      {/* 커스텀 프리셋 이름 + 프리셋 생성 버튼 (가운데 배치) */}
                      <div className="flex items-center gap-2 min-w-0">
                        {currentExam?.exam_info?.exam_type === 'custom' && currentExam.exam_info.preset_name && (
                          presetExists === false ? (
                            <span
                              className="rounded-3xl border border-red-200 bg-red-50 px-3 py-1 text-sm font-medium text-red-400 cursor-not-allowed truncate min-w-0"
                              title="프리셋이 삭제되었습니다"
                            >
                              <span className="text-sm font-medium text-red-400">𝐂</span>{!isMobileView && ' (삭제됨)'}
                            </span>
                          ) : (
                            <button
                              onClick={() => presetExists === true && navigate(`/preset?highlight=${currentExam.exam_info.preset_id}`)}
                              className={`rounded-3xl border border-green-200 bg-green-50 px-3 py-1 text-sm font-medium text-green-700 transition-colors hover:bg-green-100 hover:border-green-300 truncate min-w-0 ${presetExists === null ? 'opacity-50 cursor-wait' : ''}`}
                              title="시험지제작 페이지에서 보기"
                              disabled={presetExists === null}
                            >
                              <span className="text-sm font-medium text-green-600">𝐂</span>{!isMobileView && <span className="text-xs"> {currentExam.exam_info.preset_name}</span>}
                            </button>
                          )
                        )}
                        {currentExam?.problems_list?.length > 0 && treeData && (
                          <button
                            onClick={() => setIsCreatePresetModalOpen(true)}
                            className="shrink-0 rounded-3xl border border-purple-200 bg-purple-50 px-3 py-1 text-sm font-medium text-purple-700 transition-colors hover:bg-purple-100 hover:border-purple-300"
                            title="시험 문제로 프리셋 생성"
                          >
                            <span className="flex items-center gap-1 whitespace-nowrap">
                              <FilePlus size={14} />
                              {!isMobileView && '새 커스텀'}
                            </span>
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => navigate(`/data/${stamp_id}`)}
                          className="!bg-blue-300 !text-blue-900 hover:!bg-blue-400"
                          icon={<ChartNoAxesCombined size={14} />}
                          iconPosition="left"
                        >
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleShareResult(currentExam?.exam_info.exam_id)}
                          className="bg-blue-600 hover:bg-blue-700"
                          icon={<LinkIcon size={14} />}
                          iconPosition="left"
                        >
                          {/* 결과공유 */}
                        </Button>
                      </div>
                    </div>
                    {currentExam.exam_info.exam_status === 999 ? (
                      <div className="mt-3 mb-4 rounded-lg bg-gray-100 px-4 py-2">
                        <div className="flex items-center justify-between text-xs whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500">등급:</span>
                            <span className="font-semibold">{(analysis?.target_grade || 0) + 1}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500">진행률:</span>
                            <span className="font-semibold">{(calculateGradeProgress() * 100).toFixed(1)}%</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500">백분위:</span>
                            <span className="font-semibold">{((analysis?.percentage || 0) * 100).toFixed(1)}%</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500">남은 학습시간:</span>
                            <span className="font-semibold">{Math.round((analysis?.remain_time || 0) / 60)}시간</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 mb-4 rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-2">
                        <div className="flex items-center justify-center text-sm text-yellow-700">
                          테스트 결과가 아직 없습니다. 테스트를 완료하면 통계를 확인할 수 있습니다.
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="relative depth-dropdown-container">
                        <button
                          ref={depthDropdownRef}
                          onClick={() => setIsDepthDropdownOpen(!isDepthDropdownOpen)}
                          className="flex h-6 items-center gap-2 rounded-3xl border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <span>{depthTitles[selectedDepth]}</span>
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {isDepthDropdownOpen && depthDropdownRef.current && createPortal(
                          <div 
                            className="depth-dropdown-menu fixed rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5" 
                            style={{ 
                              zIndex: 999999,
                              top: depthDropdownRef.current.getBoundingClientRect().bottom + 4 + 'px',
                              left: depthDropdownRef.current.getBoundingClientRect().left + 'px',
                              width: '128px'
                            }}
                          >
                            <div className="py-1" role="menu">
                              {Object.entries(depthTitles).map(([depth, title]) => (
                                <button
                                  key={depth}
                                  onClick={() => {
                                    setSelectedDepth(depth);
                                    setIsDepthDropdownOpen(false);
                                    // d1, d2, d3 선택 시 출제문제만 보기 해제
                                    if (depth !== 'd4' && showOnlyProblems) {
                                      setShowOnlyProblems(false);
                                      setIsRowFixMode(false);
                                      setFixedRows([]);
                                      setSelectedRows([]);
                                      setIsSelectingRows(false);
                                    }
                                  }}
                                  className={`block w-full px-4 py-2 text-left text-sm hover:bg-gray-100 ${
                                    selectedDepth === depth ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                                  }`}
                                  role="menuitem"
                                >
                                  {title}
                                </button>
                              ))}
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {!isCreateExamModalOpen && selectedDepth === 'd4' && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">
                              출제 문제만
                            </span>
                            <button
                              onClick={() => {
                                setShowOnlyProblems(!showOnlyProblems);
                                if (!showOnlyProblems) {
                                  setIsRowFixMode(false);
                                  setFixedRows([]);
                                  setSelectedRows([]);
                                  setIsSelectingRows(false);
                                }
                              }}
                              className={`
                                relative inline-flex h-6 w-11 items-center rounded-full
                                ${showOnlyProblems ? 'bg-primary' : 'bg-gray-200'}
                                transition-colors duration-200
                              `}
                            >
                              <span
                                className={`
                                  inline-block size-4 rounded-full bg-white transition-transform duration-200
                                  ${showOnlyProblems ? 'translate-x-6' : 'translate-x-1'}
                                `}
                              />
                            </button>
                            {selectedDepth === 'd4' && (
                              <div className="ml-2 flex items-center gap-2">
                                {isSelectingRows ? (
                                  <>
                                    <Button
                                      variant="primary"
                                      size="sm"
                                      onClick={handleFixRows}
                                    >
                                      선택 완료
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => {
                                        setIsSelectingRows(false);
                                        setSelectedRows([]);
                                      }}
                                    >
                                      취소
                                    </Button>
                                  </>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600">행 고정</span>
                                    {isRowFixMode ? (
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={handleClearFixedRows}
                                      >
                                        고정 해제
                                      </Button>
                                    ) : (
                                      <button
                                        onClick={() => {
                                          setIsSelectingRows(true);
                                          // 행 고정 모드 활성화 시 모바일에서 중유형 컬럼으로 스크롤
                                          if (mobileScrollRef.current && window.innerWidth < 768) {
                                            setTimeout(() => {
                                              if (mobileScrollRef.current) {
                                                const table = mobileScrollRef.current.querySelector('table');
                                                if (table) {
                                                  const headers = table.querySelectorAll('thead th');
                                                  if (headers.length > 0) {
                                                    const lastHeader = headers[headers.length - 1];
                                                    mobileScrollRef.current.scrollLeft = lastHeader.offsetLeft;
                                                  }
                                                }
                                              }
                                            }, 100);
                                          }
                                        }}
                                        className={`
                                          relative inline-flex h-6 w-11 items-center rounded-full
                                          ${isRowFixMode ? 'bg-primary' : 'bg-gray-200'}
                                          transition-colors duration-200
                                        `}
                                      >
                                        <span
                                          className={`
                                            inline-block size-4 rounded-full bg-white transition-transform duration-200
                                            ${isRowFixMode ? 'translate-x-6' : 'translate-x-1'}
                                          `}
                                        />
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      {renderTable()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
      </div>

      <ExamListModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        examList={examList}
        onSelectExam={handleSelectExam}
        stampId={stamp_id}
        onRefreshExamList={async () => {
          // 시험 목록만 경량 갱신 (상세/결과맵 연쇄 로드 방지)
          try {
            const token = await getAccessToken();
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp_id}`, {
              headers: { 'Authorization': token }
            });
            if (response.ok) {
              const data = await response.json();
              setExamList(data.data || []);
            }
          } catch (error) {
            console.error('시험 목록 갱신 오류:', error);
          }
        }}
        onWrongAnswerGenerated={async () => {
          // 오답노트 생성 후에도 동일한 경량 갱신
          try {
            const token = await getAccessToken();
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stamp_id}`, {
              headers: { 'Authorization': token }
            });
            if (response.ok) {
              const data = await response.json();
              setExamList(data.data || []);
            }
          } catch (error) {
            console.error('시험 목록 갱신 오류:', error);
          }
        }}
        onExamCreated={() => {
          // Epsilon 출제 성공: 모달 닫기 + 시험 목록 갱신 (최신 시험 = 새 TID로 자동 이동)
          setIsModalOpen(false);
          fetchExamList();
        }}
      />

      <ResultInputModal
        isOpen={isResultModalOpen}
        onClose={() => {
          setIsResultModalOpen(false);
          setIsConfirming(false);
          setSelectedExamId(null);
        }}
        examId={selectedExamId}
        stampId={stamp_id}
        onRefresh={(examId) => fetchExamDetail(examId)}
        isConfirmMode={isConfirming}
      />

      <CreateExamModal
        ref={modalRef}
        isOpen={isCreateExamModalOpen}
        onClose={handleCloseCreateExamModal}
        stampId={stamp_id}
        buttonRect={floatingButtonRect}
        onSuccess={() => {
          fetchExamList();
          setIsCreateExamModalOpen(false);
          setSelectedAddressesForExam([]);
          setDeltaSelections([]);
          setExamType('');
          setHierarchicalSelections({
            d1: new Set(),
            d2: new Set(),
            d3: new Set(),
            d4: new Set()
          });
        }}
        selectedAddressesForExam={selectedAddressesForExam}
        setSelectedAddressesForExam={setSelectedAddressesForExam}
        examType={examType}
        setExamType={setExamType}
        deltaSelections={deltaSelections}
        setDeltaSelections={setDeltaSelections}
        treeData={treeData}
        hierarchicalSelections={hierarchicalSelections}
        setHierarchicalSelections={setHierarchicalSelections}
      />

      <WrongAnswerModal
        isOpen={isWrongAnswerModalOpen}
        onClose={() => setIsWrongAnswerModalOpen(false)}
        examList={examList}
        stampId={stamp_id}
        tree_name={currentExam?.exam_info?.tree_name}
        edu_ver={currentExam?.exam_info?.edu_ver}
        student_name={currentExam?.student_info?.student_name}
      />

      <ProblemPreview
        isOpen={previewInfo.isOpen}
        onClose={() => setPreviewInfo({
          isOpen: false,
          problemId: [],
          problemNumber: null,
          isCorrect: null
        })}
        problemIds={previewInfo.problemId}
        showNumbers={!!previewInfo.problemNumber}
        correctList={previewInfo.isCorrect || []}
        problemNumbers={previewInfo.problemNumber || []}
      />

      {/* 시험지 미리보기 모달 */}
      <PrintPreviewModal
        isOpen={showPrintPreview}
        onClose={() => setShowPrintPreview(false)}
        testData={currentExam ? {
          ...currentExam.exam_info,
          problems_list: currentExam.problems_list,
          sequence: currentExam.sequence,
          result: currentExam.result,
          student_name: currentExam.student_info?.student_name
        } : null}
        isMobileView={window.innerWidth < 768}
      />

      {/* 카이리포트 모달 */}
      {currentExam && (
        <ReportModal
          isOpen={isReportModalOpen}
          onClose={() => setIsReportModalOpen(false)}
          examData={{
            stamp_id: stamp_id,
            exam_id: currentExam.exam_info.exam_id
          }}
        />
      )}


      {/* 프리셋 생성 모달 */}
      {isCreatePresetModalOpen && (
        <PresetModal
          isOpen={isCreatePresetModalOpen}
          onClose={() => setIsCreatePresetModalOpen(false)}
          stamp={getPresetStamp()}
          onSaveSuccess={handlePresetSaveSuccess}
          initialProblems={currentExam?.problems_list}
          initialPresetName={getAutoPresetName()}
          onChangeExamProblems={
            currentExam?.exam_info?.exam_status !== 999
              ? handleChangeExamProblems
              : undefined
          }
        />
      )}
      </Layout>
    </>
  );
}

export default ExamResult;